import dayjs from "dayjs";
import { useEffect, useState } from "react";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

const TICK_INTERVAL = 50;

export interface TimeKeeperProps {
    start: string,
    duration: number,
    remainingMillisecondsOnPause?: number | null,
    resume?: string | null,
};

export const useTimeKeeper = ({ start, duration, remainingMillisecondsOnPause, resume  }: TimeKeeperProps) => {
    const isPaused = !resume && !!remainingMillisecondsOnPause,
        isResumed = !!resume && !!remainingMillisecondsOnPause;

    const calculateEndTime = () =>
        isResumed ?
            dayjs(resume).add(remainingMillisecondsOnPause!, "millisecond") :
            isPaused ?
                dayjs().add(remainingMillisecondsOnPause!, "millisecond") :
                dayjs(start || dayjs()).add(duration, "minute");

    const calculateRemainingTime = () => {
        const timeRemainingBeforeEndTime = dayjs.duration(calculateEndTime().diff(dayjs()));
        // If we have a start date that's more than `duration` in the past, the
        // time remaining before the end of the heat is negative. Without the
        // following check, the first render of the hook will return that
        // incorect value just before the `useEffect` triggers a re-render via
        // the initial call to `tick`.
        return timeRemainingBeforeEndTime.asMilliseconds() <= 0
            ? dayjs.duration(0)
            : timeRemainingBeforeEndTime
        ;
    };

    const [remainingTime, setRemainingTime] = useState(calculateRemainingTime());

    useEffect(() => {
        const tick = () => {
            const calculatedRemainingTime = calculateRemainingTime();
            if ((!isPaused || (isPaused && calculatedRemainingTime.asMilliseconds() !== remainingMillisecondsOnPause))) {
                setRemainingTime(calculatedRemainingTime);
            }
        };

        tick();
        const interval = setInterval(tick, TICK_INTERVAL);

        return () => clearInterval(interval);
    }, [start, duration, resume, remainingMillisecondsOnPause]);

    return remainingTime;
};
